/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { Endpoint } from '../index';
import { z, ZodTypes } from '../zod';

const Params = z.strictObject({ organizationId: ZodTypes.Id, projectId: ZodTypes.Id });

export const GetProject = {
  id: 'project.get',
  tag: 'Project',
  summary: 'Get project',
  description: 'Retrieve a project within an organization',
  method: 'get',
  flags: { isAuthorized: true },
  route: (params: z.infer<typeof Params>) => `/organization/${params.organizationId}/project/${params.projectId}`,
  request: { params: Params },
  response: {
    200: {
      description: 'successful',
      content: {
        'application/json': {
          schema: z.strictObject({
            _links: z.strictObject({
              self: z.strictObject({ href: z.string() }),
              organization: z.strictObject({ href: z.string() }),
              thumbnail: z.strictObject({ href: z.string() }).optional(),
            }),
            id: ZodTypes.Id,
            name: ZodTypes.KubernetesIdentifier,
            description: z.string().min(1).nullable(),
            createdAt: z.date(),
            creator: z
              .strictObject({
                id: ZodTypes.Id,
                firstName: z.string().min(1),
                middleName: z.string().min(1).nullish(),
                lastName: z.string().min(1),
              })
              .nullable(),
            thumbnail: z
              .strictObject({
                id: ZodTypes.Id,
                seed: z.string().min(1).optional(),
              })
              .nullable()
              .optional(),
            clusters: z.array(
              z.strictObject({
                id: ZodTypes.Id,
                name: z.string().min(1),
                description: z.string().min(1).nullable().optional(),
                creatorId: ZodTypes.Id.nullable(),
              }),
            ),

            users: z.array(
              z.strictObject({
                id: ZodTypes.Id,
                firstName: z.string().min(1),
                lastName: z.string().min(1),
                email: z.string().email(),
                role: ZodTypes.RBAC.Project.Roles,
              }),
            ),
            teams: z.array(
              z.strictObject({
                identifier: ZodTypes.Team.Identifier,
                displayName: ZodTypes.Team.DisplayName,
                about: ZodTypes.Team.About.nullable(),
                role: ZodTypes.RBAC.Project.Roles,
              }),
            ),
          }),
        },
      },
    },
  },
} as const satisfies Endpoint;
